import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  boardLayout, drawCells, drawClueAreas, drawGridLines, prepareCanvas,
} from './nonoDraw';
import {
  applyTransform, autoThreshold, baseScale, cellDarkness, centeredTransform, silhouetteOf, thresholdCells,
} from '../../utils/nonoRaster';
import { analyze } from '../../utils/nonoSolver';
import GridSizeInputs from './GridSizeInputs';
import { canvasPoint, TracerActions, TracerCanvas, TracerStatus, useTracerKeys } from './tracerShared';

const HANDLE = 7;
const HIT = 11;
const ROTATE_OFFSET = 28;

// Places a source image over the grid: drag to move, corners scale, sides stretch, the top knob rotates
// (Shift snaps to 15°), the wheel zooms (Shift+wheel rotates). The raster preview updates as you go.
function NonoTracer({ width, height, source: original, autoContrast, fillable, maxWidth, onResizeGrid, onApply, onCancel }) {
  const canvasRef = useRef(null);
  const drag = useRef(null);
  const [t, setT] = useState(() => centeredTransform(width, height));
  const [threshold, setThreshold] = useState(null);
  const [invert, setInvert] = useState(false);
  const [showSource, setShowSource] = useState(true);
  const [fill, setFill] = useState(!!fillable);
  const filled = useMemo(() => (fillable ? silhouetteOf(original) : null), [fillable, original]);
  const source = fill && filled ? filled : original;
  const layout = boardLayout(width, height, maxWidth, 640);

  // A new grid size recenters the picture; threshold and the other settings stay.
  useEffect(() => { setT(centeredTransform(width, height)); }, [width, height]);

  const darkness = useMemo(() => cellDarkness(source, width, height, t), [source, width, height, t]);
  // The first threshold comes from the picture itself; after that the slider is the admin's.
  const level = threshold ?? (autoContrast ? autoThreshold(darkness) : 0.45);
  const cells = useMemo(() => thresholdCells(darkness, level, invert), [darkness, level, invert]);
  const analysis = useMemo(() => analyze(width, height, cells), [width, height, cells]);

  const geometry = useCallback(() => {
    const s = baseScale(source, width, height) * t.scale;
    const ax = s * t.stretchX, ay = s * t.stretchY;
    const rad = (t.rotation * Math.PI) / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    const toScreen = (u, v) => ({
      x: layout.left + (t.x + cos * u * ax - sin * v * ay) * layout.cell,
      y: layout.top + (t.y + sin * u * ax + cos * v * ay) * layout.cell,
    });
    const hw = source.width / 2, hh = source.height / 2;
    const top = toScreen(0, -hh);
    const handles = [
      { kind: 'scale', ...toScreen(-hw, -hh) }, { kind: 'scale', ...toScreen(hw, -hh) },
      { kind: 'scale', ...toScreen(hw, hh) }, { kind: 'scale', ...toScreen(-hw, hh) },
      { kind: 'stretchX', ...toScreen(-hw, 0) }, { kind: 'stretchX', ...toScreen(hw, 0) },
      { kind: 'stretchY', ...top }, { kind: 'stretchY', ...toScreen(0, hh) },
      { kind: 'rotate', x: top.x + sin * ROTATE_OFFSET, y: top.y - cos * ROTATE_OFFSET },
    ];
    return {
      center: toScreen(0, 0),
      corners: [toScreen(-hw, -hh), toScreen(hw, -hh), toScreen(hw, hh), toScreen(-hw, hh)],
      top,
      handles,
      axisX: { x: cos, y: sin },
      axisY: { x: -sin, y: cos },
    };
  }, [source, width, height, t, layout.left, layout.top, layout.cell]);

  useEffect(() => {
    const ctx = prepareCanvas(canvasRef.current, layout);
    drawClueAreas(ctx, layout, analysis.clues, null);
    ctx.fillStyle = '#fff';
    ctx.fillRect(layout.left, layout.top, width * layout.cell, height * layout.cell);
    drawCells(ctx, layout, cells, { unresolved: analysis.status === 'empty' ? null : analysis.unresolvedCells, alpha: 0.85 });
    if (showSource) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, layout.width, layout.height);
      ctx.clip();
      ctx.translate(layout.left, layout.top);
      ctx.scale(layout.cell, layout.cell);
      applyTransform(ctx, source, width, height, t);
      ctx.globalAlpha = 0.35;
      ctx.drawImage(source, -source.width / 2, -source.height / 2);
      ctx.restore();
    }
    drawGridLines(ctx, layout);

    const g = geometry();
    ctx.strokeStyle = '#2f5bea';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.beginPath();
    g.corners.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);
    const knob = g.handles[g.handles.length - 1];
    ctx.beginPath();
    ctx.moveTo(g.top.x, g.top.y);
    ctx.lineTo(knob.x, knob.y);
    ctx.stroke();
    g.handles.forEach(h => {
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      if (h.kind === 'rotate') ctx.arc(h.x, h.y, HANDLE, 0, Math.PI * 2);
      else ctx.rect(h.x - HANDLE / 2 - 1, h.y - HANDLE / 2 - 1, HANDLE + 2, HANDLE + 2);
      ctx.fill();
      ctx.stroke();
    });
  });

  const point = (e) => canvasPoint(canvasRef.current, e);

  const onPointerDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    const p = point(e);
    const g = geometry();
    const handle = g.handles.find(h => Math.hypot(h.x - p.x, h.y - p.y) <= HIT);
    drag.current = { kind: handle?.kind || 'move', p0: p, t0: t, g };
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const p = point(e);
    const { center, axisX, axisY } = d.g;
    const rel = (q) => ({ x: q.x - center.x, y: q.y - center.y });
    const dot = (a, b) => a.x * b.x + a.y * b.y;
    const next = { ...d.t0 };
    if (d.kind === 'move') {
      next.x = d.t0.x + (p.x - d.p0.x) / layout.cell;
      next.y = d.t0.y + (p.y - d.p0.y) / layout.cell;
    } else if (d.kind === 'scale') {
      const ratio = Math.hypot(rel(p).x, rel(p).y) / Math.max(1, Math.hypot(rel(d.p0).x, rel(d.p0).y));
      next.scale = Math.max(0.05, d.t0.scale * ratio);
    } else if (d.kind === 'stretchX' || d.kind === 'stretchY') {
      const axis = d.kind === 'stretchX' ? axisX : axisY;
      const start = dot(rel(d.p0), axis);
      const ratio = Math.abs(dot(rel(p), axis) / (Math.abs(start) < 1 ? 1 : start));
      next[d.kind] = Math.max(0.05, d.t0[d.kind] * ratio);
    } else if (d.kind === 'rotate') {
      const angle = (q) => (Math.atan2(rel(q).y, rel(q).x) * 180) / Math.PI;
      let rotation = d.t0.rotation + angle(p) - angle(d.p0);
      if (e.shiftKey) rotation = Math.round(rotation / 15) * 15;
      next.rotation = ((rotation + 540) % 360) - 180;
    }
    setT(next);
  };

  const onWheel = useCallback((e) => {
    e.preventDefault();
    setT(prev => (e.shiftKey
      ? { ...prev, rotation: ((prev.rotation + Math.sign(e.deltaY) * 5 + 540) % 360) - 180 }
      : { ...prev, scale: Math.max(0.05, prev.scale * Math.exp(-e.deltaY * 0.0012)) }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  const apply = useCallback(() => onApply(cells), [onApply, cells]);

  useTracerKeys({
    apply,
    onCancel,
    onKey: (e) => {
      const step = e.shiftKey ? 1 : 0.25;
      const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      if (moves[e.key]) {
        e.preventDefault();
        setT(prev => ({ ...prev, x: prev.x + moves[e.key][0], y: prev.y + moves[e.key][1] }));
      } else if (e.key === '+' || e.key === '=') {
        setT(prev => ({ ...prev, scale: prev.scale * 1.06 }));
      } else if (e.key === '-') {
        setT(prev => ({ ...prev, scale: prev.scale / 1.06 }));
      } else if (e.key === '[' || e.key === ']') {
        setT(prev => ({ ...prev, rotation: ((prev.rotation + (e.key === ']' ? 5 : -5) + 540) % 360) - 180 }));
      } else if (e.key.toLowerCase() === 'i' && !e.ctrlKey && !e.metaKey) {
        setInvert(v => !v);
      } else if (e.key.toLowerCase() === 'f' && fillable && !e.ctrlKey && !e.metaKey) {
        setFill(v => !v);
      }
    },
  });

  const slider = (label, key, min, max, stepSize, format = v => v.toFixed(2)) => (
    <label className="nn-slider">
      <span>{label}<b>{format(t[key])}</b></span>
      <input type="range" min={min} max={max} step={stepSize} value={t[key]}
        onChange={e => setT(prev => ({ ...prev, [key]: Number(e.target.value) }))} />
    </label>
  );

  return (
    <div className="nn-tracer">
      <TracerCanvas canvasRef={canvasRef} drag={drag} onPointerDown={onPointerDown} onPointerMove={onPointerMove} />
      <div className="nn-tracer-panel">
        <label className="nn-slider">
          <span>Kynnys<b>{Math.round(level * 100)} %</b></span>
          <input type="range" min={0.03} max={0.97} step={0.01} value={level} onChange={e => setThreshold(Number(e.target.value))} />
        </label>
        {fillable && (
          <label className="nn-check" title="Täyttää ääriviivojen sisään jäävät alueet (F)">
            <input type="checkbox" checked={fill} onChange={e => setFill(e.target.checked)} /> Täytä muodot <kbd>F</kbd>
          </label>
        )}
        <div className="nn-row">
          <button className="btn-secondary" onClick={() => setThreshold(autoThreshold(darkness))}>Automaattinen</button>
          <label className="nn-check"><input type="checkbox" checked={invert} onChange={e => setInvert(e.target.checked)} /> Käänteinen <kbd>I</kbd></label>
        </div>
        <GridSizeInputs width={width} height={height} onResize={onResizeGrid} />
        {slider('Koko', 'scale', 0.1, 4, 0.01)}
        {slider('Venytys vaaka', 'stretchX', 0.2, 3, 0.01)}
        {slider('Venytys pysty', 'stretchY', 0.2, 3, 0.01)}
        {slider('Kierto', 'rotation', -180, 180, 1, v => `${Math.round(v)}°`)}
        <div className="nn-row">
          <button className="btn-secondary" onClick={() => setT(centeredTransform(width, height))}>Keskitä</button>
          <label className="nn-check"><input type="checkbox" checked={showSource} onChange={e => setShowSource(e.target.checked)} /> Näytä lähde</label>
        </div>
        <TracerStatus analysis={analysis} />
        <TracerActions onCancel={onCancel} apply={apply} />
        <p className="nn-hint">Raahaa siirtääksesi, kulmat skaalaavat, sivut venyttävät, yläpallo kiertää (Shift = 15°). Rulla zoomaa, Shift+rulla kiertää. Nuolet siirtävät, + − koko, [ ] kierto.</p>
      </div>
    </div>
  );
}

export default NonoTracer;
